import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  createEntitlementsApi,
  EntitlementAction,
} from "../api/entitlementsApi";
import { hasEntitlementBillingAccess } from "../utils/orgBillingAccess";
import {
  hasAdministratorRole,
  hasBillingAccess,
  hasSubscriptionManagerRole,
  hasSuperAdministratorRole,
} from "../utils/roleUtils";
import useAuth from "./useAuth";
import { auth0Client } from "../contexts/auth0Client";
import { useSelector } from "../store";
import useRoles from "./useRoles";

const useEntitlements = () => {
  const { isInitialized } = useAuth();
  const org = useSelector((state) => state.user.organization);
  const userRoles = useRoles();

  const api = useMemo(
    () =>
      isInitialized && auth0Client ? createEntitlementsApi(auth0Client) : null,
    [isInitialized],
  );

  const {
    data: entitlements,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["entitlements", org, api],
    queryFn: () => api!.getEntitlements(org!.authId),
    enabled: !!api && !!org?.authId,
    staleTime: 60 * 1000, // 1 minute
  });

  const can = useCallback(
    (action: EntitlementAction | string) => {
      if (!entitlements) return false;

      // Define roles for both capability and explicit checks
      const isSubManager = hasSubscriptionManagerRole(userRoles);
      const isAdmin = hasAdministratorRole(userRoles);
      const isSuperAdmin = hasSuperAdministratorRole(userRoles);
      const isClinicalRevenueAdmin = hasBillingAccess(userRoles ?? []);

      // Patient billing: org capability (legacy virtual or onboarded BILLING addon)
      // + Clinical Revenue Administrator. Do not require subscription onboarding,
      // and do not fall through to the Super Admin default.
      if (
        action === EntitlementAction.BILLING_ACCESS ||
        action === "billing_access"
      ) {
        if (!isClinicalRevenueAdmin || entitlements.isRestricted) {
          return false;
        }
        return hasEntitlementBillingAccess(entitlements.capabilities);
      }

      // SUBSCRIPTION_MANAGEMENT stays available when restricted so Subscription
      // Managers can pay and restore access. Administrator and Super
      // Administrator are not part of the organization payment-method flow.
      if (action === EntitlementAction.SUBSCRIPTION_MANAGEMENT) {
        return isSubManager;
      }

      // MEMBER_MANAGEMENT and REMOVE_MEMBER must remain available even if restricted,
      // but only for manager/admin/super admin so they can update the plan or remove
      // members to restore access.
      if (
        action === EntitlementAction.MEMBER_MANAGEMENT ||
        action === EntitlementAction.REMOVE_MEMBER
      ) {
        return isSubManager || isAdmin || isSuperAdmin;
      }

      // Check direct capabilities first
      // These are defined in the Entitlement record as feature flags
      if (entitlements.capabilities) {
        const cap = entitlements.capabilities[action];
        if (cap && typeof cap === "object" && "enabled" in cap) {
          if (cap.enabled === true) {
            return !entitlements.isRestricted;
          }
        } else if (cap && typeof cap === "boolean") {
          return cap;
        }
      }

      if (entitlements.isRestricted) return false;

      if (action === EntitlementAction.ADD_MEMBER) {
        return isSubManager || isAdmin || isSuperAdmin;
      }

      // Default: Super Admin can see everything that isn't explicitly handled or restricted
      if (isSuperAdmin) return true;

      return false;
    },
    [entitlements, userRoles],
  );

  const isOnboarded = useCallback(
    (feature: string) => {
      if (!entitlements?.rolloutFlags) return false;
      return entitlements.rolloutFlags[feature] === true;
    },
    [entitlements],
  );

  return {
    entitlements,
    isLoading,
    error,
    can,
    isOnboarded,
  };
};

export default useEntitlements;
